import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

interface Brand {
  id: string;
  business_id: string;
  name: string;
  short_code: string | null;
  channel_type: "direct" | "retail_partner" | "wholesale" | null;
  partner_name: string | null;
  logo_url: string | null;
  icon_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  branding_config: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BrandsResponse {
  brands: Brand[];
}

interface BrandResponse {
  brand: Brand;
}

export type { Brand };

export interface CreateBrandInput {
  name: string;
  short_code?: string;
  channel_type?: "direct" | "retail_partner" | "wholesale";
  partner_name?: string;
  logo_url?: string;
  icon_url?: string;
  primary_color?: string;
  secondary_color?: string;
}

interface UpdateBrandInput extends Partial<CreateBrandInput> {}

const BRANDS_KEY = ["brands"];

export function useBrands() {
  return useQuery({
    queryKey: BRANDS_KEY,
    queryFn: () => api.get<BrandsResponse>("/core/brands"),
  });
}

export function useCreateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBrandInput) =>
      api.post<BrandResponse>("/core/brands", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRANDS_KEY });
    },
  });
}

export function useUpdateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateBrandInput & { id: string }) =>
      api.patch<BrandResponse>(`/core/brands/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRANDS_KEY });
    },
  });
}

export function useDeactivateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ success: boolean }>(`/core/brands/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRANDS_KEY });
    },
  });
}
